import { useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { useAuth } from "@/App";
import apiClient from "@/utils/apiClient";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Award, CreditCard, Crown, Check, 
  ChevronRight, Shield, Bell, LogOut
} from "lucide-react";

const SUBSCRIPTION_PLANS = [
  {
    id: "starter-plus",
    name: "Starter+",
    price: 9.99,
    features: [
      "Unlimited Mood Tracking",
      "Full Blog & Digest Access",
      "Daily ME.WELL Mind-SPA Ai.2 Agent",
      "Wellness Streak Bonus Rewards",
      "200 Welcome SMAHOO Coins"
    ]
  },
  {
    id: "premium",
    name: "Premium Membership",
    price: 49.99,
    features: [
      "Everything in Starter+",
      "Multi-AI Coach (GPT, Claude, Gemini)",
      "Premium Streaming Content",
      "Priority OnCALL+ Live Access",
      "500 Welcome + Monthly SMAHOO Coin Bonus"
    ],
    popular: true
  },
  {
    id: "elite",
    name: "Elite Transformation",
    price: 99.99,
    features: [
      "Everything in Premium",
      "1 Check-IN & Tune-UP monthly 1-on-1 session",
      "Mind-SPA Retreat Program Access",
      "Priority OnCALL+ support",
      "3x SMAHOO Coins rewards"
    ]
  }
];

const TIER_COLORS = {
  elite: 'text-yellow-500',
  premium: 'text-purple-500',
  basic: 'text-spa-sage',
  free: 'text-spa-text-secondary'
};

export default function Profile() {
  const { user, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const handleUpgrade = async (planId) => {
    setIsLoading(true);
    try {
      const response = await apiClient.post('/payments/checkout/session', {
        package_id: planId,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      // silent
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  const currentTier = user?.subscription_tier || 'free';

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="profile-page">
        {/* Header */}
        <div>
          <p className="overline-text mb-2">Account Settings</p>
          <h1 className="font-serif text-3xl sm:text-4xl font-light text-spa-moss">
            Profile & Subscriptions
          </h1>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile Info */}
          <div className="space-y-6">
            {/* User Card */}
            <div className="spa-card" data-testid="user-card">
              <div className="flex flex-col items-center text-center">
                <Avatar className="w-24 h-24 mb-4">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-spa-terracotta text-white font-serif text-3xl">
                    {user?.name?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <h2 className="font-serif text-2xl text-spa-moss mb-1">{user?.name}</h2>
                <p className="text-spa-text-secondary text-sm mb-4">{user?.email}</p>
                
                <div className="flex items-center gap-2 px-4 py-2 bg-spa-muted rounded-full">
                  <Crown className={`w-4 h-4 ${TIER_COLORS[currentTier]}`} />
                  <span className="text-sm font-medium text-spa-moss capitalize">
                    {currentTier} Plan
                  </span>
                </div>
              </div>
            </div>

            {/* SMAHOO Coins */}
            <div className="spa-card" data-testid="coins-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-lg text-spa-moss">SMAHOO Coins</h3>
                <Award className="w-5 h-5 text-spa-terracotta" />
              </div>
              <p className="font-serif text-4xl text-spa-moss coins-glow mb-2">
                {user?.smahoo_coins?.toFixed(0) || 0}
              </p>
              <p className="text-sm text-spa-text-secondary">Available balance</p>
            </div>

            {/* Quick Actions */}
            <div className="spa-card space-y-2">
              <button className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-spa-muted transition-colors">
                <span className="flex items-center gap-3 text-spa-moss">
                  <Bell className="w-5 h-5 text-spa-text-secondary" />
                  Notifications
                </span>
                <ChevronRight className="w-4 h-4 text-spa-text-secondary" />
              </button>
              <button className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-spa-muted transition-colors">
                <span className="flex items-center gap-3 text-spa-moss">
                  <Shield className="w-5 h-5 text-spa-text-secondary" />
                  Privacy & Security
                </span>
                <ChevronRight className="w-4 h-4 text-spa-text-secondary" />
              </button>
              <button 
                onClick={handleLogout}
                className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-red-50 text-red-600 transition-colors"
                data-testid="logout-button"
              >
                <span className="flex items-center gap-3">
                  <LogOut className="w-5 h-5" />
                  Logout
                </span>
              </button>
            </div>
          </div>

          {/* Subscription Plans */}
          <div className="lg:col-span-2">
            <h2 className="font-serif text-2xl text-spa-moss mb-6">Subscription Plans</h2>
            
            <div className="grid md:grid-cols-3 gap-4">
              {SUBSCRIPTION_PLANS.map((plan) => {
                const isCurrentPlan = currentTier === plan.id;
                return (
                  <div 
                    key={plan.id}
                    className={`spa-card relative ${
                      plan.popular ? 'ring-2 ring-spa-terracotta' : ''
                    } ${isCurrentPlan ? 'bg-spa-sage/5' : ''}`}
                    data-testid={`plan-${plan.id}`}
                  >
                    {plan.popular && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-spa-terracotta text-white text-xs px-3 py-1 rounded-full">
                        Most Popular
                      </span>
                    )}
                    
                    <div className="text-center mb-6">
                      <h3 className="font-serif text-xl text-spa-moss mb-2">{plan.name}</h3>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="font-serif text-3xl text-spa-moss">CA${plan.price}</span>
                        <span className="text-spa-text-secondary">/mo</span>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-start gap-2 text-sm">
                          <Check className="w-4 h-4 text-spa-sage flex-shrink-0 mt-0.5" />
                          <span className="text-spa-text-secondary">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      onClick={() => !isCurrentPlan && handleUpgrade(plan.id)}
                      disabled={isCurrentPlan || isLoading}
                      className={`w-full ${
                        isCurrentPlan 
                          ? 'bg-spa-sage text-white cursor-default' 
                          : plan.popular 
                            ? 'spa-button-primary' 
                            : 'spa-button-secondary'
                      }`}
                      data-testid={`upgrade-${plan.id}`}
                    >
                      {isCurrentPlan ? 'Current Plan' : 'Upgrade'}
                    </Button>
                  </div>
                );
              })}
            </div>

            {/* Payment History */}
            <div className="spa-card mt-8" data-testid="payment-history">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif text-xl text-spa-moss">Payment History</h3>
                <CreditCard className="w-5 h-5 text-spa-text-secondary" />
              </div>
              
              <div className="text-center py-8 text-spa-text-secondary">
                <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No payment history available</p>
                <p className="text-xs mt-1">Your payment records will appear here</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
